
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';

const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items: cart, updateQuantity, removeFromCart, totalPrice } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount: number } | null>(null);

  const subtotal = totalPrice;
  const shipping = subtotal > 500 ? 0 : 50;
  const discount = appliedCoupon ? (subtotal * appliedCoupon.discount) / 100 : 0;
  const total = subtotal + shipping - discount;

  const applyCoupon = () => {
    if (couponCode === 'OMRAN10') {
      setAppliedCoupon({ code: couponCode, discount: 10 });
    } else if (couponCode === 'OMRAN20') {
      setAppliedCoupon({ code: couponCode, discount: 20 });
    } else {
      alert('كود الخصم غير صحيح');
    }
  };

  const handleCheckout = () => {
    if (!user) {
      if (confirm('يجب تسجيل الدخول لإتمام عملية الشراء. هل تريد تسجيل الدخول الآن؟')) {
        navigate('/login');
      }
    } else {
      navigate('/checkout');
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center max-w-2xl mx-auto">
            <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shopping-cart-line text-6xl text-gray-400"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">سلة التسوق فارغة</h2>
            <p className="text-gray-600 mb-8 text-lg">لم تقم بإضافة أي منتجات بعد</p>
            <Link
              to="/categories"
              className="inline-flex items-center gap-2 bg-[#0D47A1] text-white px-8 py-4 rounded-lg font-medium hover:bg-[#0a3a8a] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line"></i>
              تصفح المنتجات
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />

      <div className="container mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-gray-800 mb-8">سلة التسوق</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex gap-6">
                  <Link to={`/product/${item.id}`} className="flex-shrink-0 cursor-pointer">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-32 h-32 object-cover rounded-xl"
                    />
                  </Link>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <Link to={`/product/${item.id}`} className="cursor-pointer">
                          <h3 className="text-xl font-bold text-gray-800 hover:text-[#0D47A1] transition-colors">
                            {item.name}
                          </h3>
                        </Link>
                        <p className="text-gray-600">{item.brand}</p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="w-10 h-10 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors cursor-pointer flex items-center justify-center"
                      >
                        <i className="ri-delete-bin-line text-xl"></i>
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center border-2 border-gray-200 rounded-lg">
                        <button
                          onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                          className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.id, Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-16 h-10 text-center border-x-2 border-gray-200 focus:outline-none"
                        />
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>

                      <div className="text-left">
                        <p className="text-2xl font-bold text-[#0D47A1]">
                          {(item.price * item.quantity).toLocaleString('ar-SA')} ريال
                        </p>
                        <p className="text-sm text-gray-600">
                          {item.price.toLocaleString('ar-SA')} ريال للقطعة
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">ملخص الطلب</h2>

              {/* Coupon Code */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">كود الخصم</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="أدخل الكود"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0D47A1]"
                  />
                  <button
                    onClick={applyCoupon}
                    className="px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    تطبيق
                  </button>
                </div>
                {appliedCoupon && (
                  <div className="mt-2 flex items-center gap-2 text-green-600">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span className="text-sm">تم تطبيق كود الخصم {appliedCoupon.code}</span>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-4 mb-6 pb-6 border-b">
                <div className="flex items-center justify-between text-gray-700">
                  <span>المجموع الفرعي</span>
                  <span className="font-medium">{subtotal.toLocaleString('ar-SA')} ريال</span>
                </div>
                <div className="flex items-center justify-between text-gray-700">
                  <span>الشحن</span>
                  <span className="font-medium">
                    {shipping === 0 ? (
                      <span className="text-green-600">مجاني</span>
                    ) : (
                      `${shipping} ريال`
                    )}
                  </span>
                </div>
                {discount > 0 && (
                  <div className="flex items-center justify-between text-green-600">
                    <span>الخصم</span>
                    <span className="font-medium">-{discount.toLocaleString('ar-SA')} ريال</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-xl font-bold text-gray-800 mb-6">
                <span>الإجمالي</span>
                <span className="text-[#0D47A1]">{total.toLocaleString('ar-SA')} ريال</span>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full bg-[#0D47A1] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#0a3a8a] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-secure-payment-line text-xl"></i>
                إتمام الطلب
              </button>

              {subtotal < 500 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-[#0D47A1]">
                    <i className="ri-information-line ml-1"></i>
                    أضف منتجات بقيمة {(500 - subtotal).toLocaleString('ar-SA')} ريال للحصول على شحن مجاني
                  </p>
                </div>
              )}

              <Link
                to="/categories"
                className="block text-center text-[#0D47A1] font-medium mt-4 hover:underline cursor-pointer"
              >
                متابعة التسوق
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
